import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { ChartLine, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "./ui/button";

/**
 * 6-month trend chart of the CA split (Danseuses vs Boissons).
 * Pure SVG — no extra dependency.
 */
export default function BusinessTrendWidget({ months = 6 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/stats/business-trend", { params: { months } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [months]);

  const points = data?.points || [];

  const chart = useMemo(() => {
    if (points.length === 0) return null;
    const W = 100; // viewBox units (scaled by CSS)
    const H = 40;
    const PAD_X = 6;
    const PAD_Y = 4;
    const innerW = W - PAD_X * 2;
    const innerH = H - PAD_Y * 2;
    const maxVal = Math.max(
      1,
      ...points.map((p) => Math.max(p.total_ca_danseuses || 0, p.total_boissons_autre || 0, p.total_ca || 0)),
    );
    const xFor = (i) =>
      points.length === 1 ? W / 2 : PAD_X + (i * innerW) / (points.length - 1);
    const yFor = (v) => PAD_Y + innerH - (v / maxVal) * innerH;

    const polyline = (key, color) => {
      const coords = points.map((p, i) => `${xFor(i).toFixed(2)},${yFor(p[key] || 0).toFixed(2)}`).join(" ");
      return <polyline fill="none" stroke={color} strokeWidth="0.6" strokeLinecap="round" strokeLinejoin="round" points={coords} />;
    };

    const dots = (key, color) =>
      points.map((p, i) => (
        <circle
          key={`${key}-${i}`}
          cx={xFor(i)}
          cy={yFor(p[key] || 0)}
          r="0.9"
          fill={color}
          stroke="#09090b"
          strokeWidth="0.25"
        />
      ));

    return (
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-48"
        data-testid="trend-svg"
      >
        {/* horizontal grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={`grid-${f}`}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={PAD_Y + innerH * f}
            y2={PAD_Y + innerH * f}
            stroke="#27272a"
            strokeWidth="0.15"
            strokeDasharray="0.8 0.6"
          />
        ))}
        {/* CA total as subtle line */}
        {polyline("total_ca", "#a1a1aa")}
        {/* Danseuses */}
        {polyline("total_ca_danseuses", "#fbbf24")}
        {/* Boissons */}
        {polyline("total_boissons_autre", "#34d399")}

        {dots("total_ca", "#a1a1aa")}
        {dots("total_ca_danseuses", "#fbbf24")}
        {dots("total_boissons_autre", "#34d399")}

        {/* hover zones */}
        {points.map((p, i) => (
          <rect
            key={`z-${i}`}
            x={xFor(i) - 4}
            y={0}
            width="8"
            height={H}
            fill="transparent"
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}
            style={{ cursor: "pointer" }}
          />
        ))}
      </svg>
    );
  }, [points]);

  const last = points[points.length - 1];
  const prev = points[points.length - 2];
  const delta = last && prev ? (last.total_ca || 0) - (prev.total_ca || 0) : 0;
  const deltaPct = last && prev && prev.total_ca > 0 ? ((delta / prev.total_ca) * 100).toFixed(1) : null;

  return (
    <div className="surface p-5" data-testid="business-trend-widget">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ChartLine size={18} color="#fbbf24" weight="fill" />
            <h3 className="font-display text-lg font-medium">Tendance — 6 derniers mois</h3>
          </div>
          <div className="text-xs text-[#71717a]">
            Évolution CA total, CA danseuses et CA boissons
          </div>
        </div>
        <div className="flex items-center gap-2">
          {deltaPct !== null && (
            <div
              className="text-xs px-2 py-1 rounded border"
              style={{
                color: delta >= 0 ? "#34d399" : "#f87171",
                borderColor: delta >= 0 ? "#34d39933" : "#f8717133",
                background: delta >= 0 ? "#34d39911" : "#f8717111",
              }}
            >
              {delta >= 0 ? "↗" : "↘"} {deltaPct}% vs m-1
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="h-8 border-[#27272a] text-white hover:bg-[#1a1a21]"
            data-testid="trend-refresh"
          >
            <ArrowClockwise size={14} />
          </Button>
        </div>
      </div>

      {points.length === 0 ? (
        <div className="text-center py-10 text-[#71717a] text-sm">Chargement...</div>
      ) : (
        <>
          {chart}

          {/* X-axis labels */}
          <div className="grid mt-1 text-[10px] text-[#71717a] uppercase" style={{ gridTemplateColumns: `repeat(${points.length}, 1fr)` }}>
            {points.map((p) => (
              <div key={`${p.year}-${p.month}`} className="text-center">
                {p.label}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-4 text-xs">
            <LegendItem color="#a1a1aa" label="CA total" />
            <LegendItem color="#fbbf24" label="CA Danseuses" />
            <LegendItem color="#34d399" label="CA Boissons" />
          </div>

          {/* Hover / current point detail */}
          <div className="mt-4 border border-[#27272a] rounded-md p-3 bg-[#09090b]">
            {(() => {
              const p = hovered != null ? points[hovered] : last;
              if (!p) return null;
              return (
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="capitalize">
                    <div className="label-xs">Mois</div>
                    <div className="font-medium">{p.label}</div>
                  </div>
                  <div>
                    <div className="label-xs">CA total</div>
                    <div className="font-mono text-[#a1a1aa]">{fmtMoney(p.total_ca)}</div>
                  </div>
                  <div>
                    <div className="label-xs">Danseuses</div>
                    <div className="font-mono" style={{ color: "#fbbf24" }}>{fmtMoney(p.total_ca_danseuses)}</div>
                  </div>
                  <div>
                    <div className="label-xs">Boissons</div>
                    <div className="font-mono" style={{ color: "#34d399" }}>{fmtMoney(p.total_boissons_autre)}</div>
                  </div>
                  <div>
                    <div className="label-xs">Jours</div>
                    <div className="font-mono text-[#a1a1aa]">{p.days_saisis}</div>
                  </div>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-[#a1a1aa]">
      <span className="inline-block w-3 h-[2px] rounded" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}
